import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import {
  ReservationPhase,
  canCancelReservation,
  canChangeReservation,
  getReservationPhase,
} from '../lib/booking/reservation-phase';

const TICK_MS = 30 * 1000;
const TIME_DRIVEN_PHASES: ReservationPhase[] = ['active', 'started', 'seated'];

/**
 * The reservation's phase, re-evaluated as time passes so a page left open flips from
 * "change/cancel" to "ongoing" without a reload.
 */
export function useReservationPhase(reservation: Reservation | undefined) {
  const [now, setNow] = useState(() => Date.now());
  const phase = getReservationPhase(reservation, now);
  const timeDriven = phase !== undefined && TIME_DRIVEN_PHASES.includes(phase);

  useEffect(() => {
    if (!timeDriven) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, [timeDriven]);

  return {
    phase,
    isLocked: phase !== undefined && phase !== 'active',
    canChange: canChangeReservation(reservation, now),
    canCancel: canCancelReservation(reservation, now),
  };
}

/**
 * For the change-booking flow: send the guest back to the reservation page as soon as the
 * original reservation turns out not to be changeable (started, seated, cancelled, past, ...),
 * e.g. when the flow is opened from an old link or left open past the booked time.
 */
export function useRedirectIfNotChangeable(reservation: Reservation | undefined) {
  const router = useRouter();
  const { phase, canChange } = useReservationPhase(reservation);
  const reservationId = reservation?.objectId;

  useEffect(() => {
    if (reservationId && phase !== undefined && !canChange) {
      router.replace(`/reservation/${reservationId}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationId, phase, canChange]);
}
